const iceServers = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    // TURN servers for relaying when direct connection fails
    {
      urls: "turn:openrelay.metered.ca:80",
      username: "openrelayproject",
      credential: "openrelayproject",
    },
    {
      urls: "turn:openrelay.metered.ca:443",
      username: "openrelayproject",
      credential: "openrelayproject",
    },
    {
      urls: "turn:openrelay.metered.ca:443?transport=tcp",
      username: "openrelayproject",
      credential: "openrelayproject",
    },
  ],
};

export const createWebRTCHandler = (
  roomId,
  sendOffer,
  sendAnswer,
  sendIceCandidate,
) => {
  let peerConnection = null;
  let localStream = null;
  let localScreen = null;
  let pendingIceCandidates = []; // Queue for early ICE candidates
  let onRemoteStreamCallback = null; // Store the callback
  let onRemoteScreenCallback = null; // Store callback for screen share
  let screenTrackIds = new Set(); // Track screen sharing track IDs
  const initLocalScreenSharing = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error(
          "Screen sharing is not available. This happens when accessing via HTTP on non-localhost domains. " +
            "Please use https:// or access via localhost on this device. " +
            "Note: Screen sharing requires a secure context (HTTPS) to function properly.",
        );
      }

      const screen = await navigator.mediaDevices.getDisplayMedia({video:true, audio:false})

      localScreen = screen;
      return screen;
    } catch (error) {

      throw error;
    }
  };
  const initialLocalStream = async () => {
    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Camera/Microphone access is not available. This happens when accessing via HTTP on non-localhost domains. " +
            "Please use https:// or access via localhost on this device. " +
            "For mobile testing, you may need to set up HTTPS or use Chrome flags.",
        );
      }


      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      localStream = stream;
      return stream;
    } catch (error) {



      alert(`Camera/Microphone Error: ${error.message}`);
      throw error;
    }
  };

  const createPeerConnection = (onRemoteStream, onRemoteScreen) => {
    const pc = new RTCPeerConnection(iceServers);
    
    // Store the callbacks for later use
    if (onRemoteStream) {
      onRemoteStreamCallback = onRemoteStream;
    }
    if (onRemoteScreen) {
      onRemoteScreenCallback = onRemoteScreen;
    }
    
    // CRITICAL: Add local stream tracks to peer connection
    if (localStream) {

      localStream.getTracks().forEach((track) => {

        pc.addTrack(track, localStream);
      });
    } else {

    }

    if(localScreen){
      const screenTrack = localScreen.getVideoTracks()[0];

      pc.addTrack(screenTrack, localScreen);
      // Mark this track as screen share
      screenTrackIds.add(screenTrack.id);
    }

    pc.ontrack = (event) => {

      const stream = event.streams[0];
      
      // Check if this is a screen share track by checking if it's video-only and label contains "screen"
      const isScreenShare = event.track.kind === 'video' && 
        (event.track.label.toLowerCase().includes('screen') || 
         event.track.label.toLowerCase().includes('window') ||
         event.track.label.toLowerCase().includes('web contents'));
      
      if (isScreenShare && onRemoteScreenCallback) {

        onRemoteScreenCallback(stream);
      } else if (onRemoteStreamCallback) {
        // Regular camera/audio stream

        onRemoteStreamCallback(stream);
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendIceCandidate({
          roomId,
          candidate: event.candidate,
        });
      }
    };
    peerConnection = pc;
    return pc;
  };

  const createOffer = async (onRemoteStream, onRemoteScreen) => {
    try {


      // CRITICAL: Ensure local stream exists before creating offer
      if (!localStream) {

        throw new Error('Local stream not initialized');
      }

      // Close existing connection if any
      if (peerConnection) {
        console.log(
          "Closing existing peer connection before creating new offer",
        );
        peerConnection.close();
        peerConnection = null;
      }

      const pc = createPeerConnection(onRemoteStream, onRemoteScreen);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      sendOffer({ roomId, offer });
    } catch (error) {

      throw error;
    }
  };

  const handleOffer = async (offer, onRemoteStream, onRemoteScreen) => {
    try {


      // CRITICAL: Ensure local stream exists before handling offer
      if (!localStream) {

        throw new Error('Local stream not initialized');
      }

      // Close existing connection if any
      if (peerConnection) {
        console.log(
          "Closing existing peer connection before handling new offer",
        );
        peerConnection.close();
        peerConnection = null;
      }

      // Clear pending candidates
      pendingIceCandidates = [];

      const pc = createPeerConnection(onRemoteStream, onRemoteScreen);
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      sendAnswer({ roomId, answer });

      // Process queued ICE candidates
      if (pendingIceCandidates.length > 0) {
        console.log(
          `Processing ${pendingIceCandidates.length} queued ICE candidates`,
        );
        for (const candidate of pendingIceCandidates) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          } catch (err) {

          }
        }
        pendingIceCandidates = [];
      }
    } catch (error) {

      throw error;
    }
  };

  const handleAnswer = async (answer) => {
    try {
      if (!peerConnection) {

        return;
      }

      console.log(
        "Handling answer. Current state:",
        peerConnection.signalingState,
      );

      // Only set remote description if we're in the right state
      if (peerConnection.signalingState === "have-local-offer") {
        await peerConnection.setRemoteDescription(
          new RTCSessionDescription(answer),
        );
        console.log(
          "Remote answer set successfully. New state:",
          peerConnection.signalingState,
        );

        // Process queued ICE candidates
        if (pendingIceCandidates.length > 0) {
          console.log(
            `Processing ${pendingIceCandidates.length} queued ICE candidates`,
          );
          for (const candidate of pendingIceCandidates) {
            try {
              await peerConnection.addIceCandidate(
                new RTCIceCandidate(candidate),
              );
            } catch (err) {

            }
          }
          pendingIceCandidates = [];
        }
      } else if (peerConnection.signalingState === "stable") {

      } else {
        console.warn(
          "Cannot set remote answer. Wrong state:",
          peerConnection.signalingState,
        );
      }
    } catch (error) {

      // Don't rethrow - just log and continue
    }
  };

  const handleIceCandidate = async (candidate) => {
    try {
      if (!peerConnection) {

        pendingIceCandidates.push(candidate);
        return;
      }

      if (peerConnection.remoteDescription) {
        await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));

      } else {

        pendingIceCandidates.push(candidate);
      }
    } catch (error) {

      // Don't throw - ICE candidate failures shouldn't break the connection
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled; // Toggle on/off
        return videoTrack.enabled;
      }
    }
    return false;
  };

  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled; // Toggle on/off
        return audioTrack.enabled;
      }
    }
    return false;
  };

  const toggleScreenShare = async (isSharing) => {
    if (!peerConnection) {

      return { success: false, stream: null };
    }

    try {
      if (!isSharing) {
        // Start screen sharing
        const screenStream = await initLocalScreenSharing();
        const screenTrack = screenStream.getVideoTracks()[0];
        
        // Store screen stream
        localScreen = screenStream;
        
        // Mark this track as screen share
        screenTrackIds.add(screenTrack.id);
        
        // Add screen track to peer connection
        peerConnection.addTrack(screenTrack, screenStream);
        
        // Renegotiate connection - create new offer to notify remote peer

        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        sendOffer({ roomId, offer });
        
        // Listen for when user stops sharing via browser UI
        screenTrack.onended = () => {

          // Auto-cleanup when user stops via browser
          if (localScreen) {
            localScreen.getTracks().forEach(track => {
              track.stop();
              screenTrackIds.delete(track.id);
            });
            localScreen = null;
          }
        };
        
        return { success: true, stream: screenStream };
      } else {
        // Stop screen sharing
        const senders = peerConnection.getSenders();
        const screenSender = senders.find(sender => {
          return sender.track && screenTrackIds.has(sender.track.id);
        });
        
        if (screenSender) {
          peerConnection.removeTrack(screenSender);
          
          // Renegotiate connection - create new offer to notify remote peer

          const offer = await peerConnection.createOffer();
          await peerConnection.setLocalDescription(offer);
          sendOffer({ roomId, offer });
        }
        
        // Stop screen tracks
        if (localScreen) {
          localScreen.getTracks().forEach(track => {
            track.stop();
            screenTrackIds.delete(track.id);
          });
          localScreen = null;
        }
        
        return { success: true, stream: null };
      }
    } catch (error) {

      return { success: false, stream: null };
    }
  };

  const cleanup = () => {


    // Stop all media tracks
    if (localStream) {
      localStream.getTracks().forEach((track) => {
        track.stop();

      });
      localStream = null;
    }

    // Stop screen sharing tracks
    if (localScreen) {
      localScreen.getTracks().forEach((track) => {
        track.stop();

      });
      localScreen = null;
    }

    // Close peer connection
    if (peerConnection) {
      peerConnection.close();

      peerConnection = null;
    }
  };

  return {
    initialLocalStream,
    createOffer,
    handleOffer,
    handleAnswer,
    handleIceCandidate,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
    cleanup,
  };
};
